import type { ChartSettings, LineStyleKey, TrendRange, TrendScale, TrendVariant } from "./types";

/** The Trends page, a preview or a saved trend (`/trends/<id>`). */
export const isTrendsPath = (pathname: string | null) => pathname === "/trends" || !!pathname?.startsWith("/trends/");

/** The page holds at most this many trends. */
export const MAX_CHARTS = 6;

/**
 * At most this many different series are active across all trends. To add
 * another, the user removes one first.
 */
export const MAX_ACTIVE_SERIES = 6;

/**
 * A page opened from a dashboard shows this many of the series it starts
 * with; the rest start hidden, since six lines at once are hard to read.
 */
export const VISIBLE_ON_START = 3;

/** Longest name a chart can be given. */
export const CHART_NAME_MAX_LENGTH = 80;

/** Width of the series sidebar inside each trend card, in px. */
export const SERIES_SIDEBAR_WIDTH = { min: 180, max: 420, default: 240 };

/** Volya band colors, low to high (same as the gauge segments). */
export const BAND_COLORS = ["#6E99C3", "#6CB9B0", "#8FAE51", "#E8BE00", "#DDA51B", "#EC5838", "#DA2E83"];

export const BAND_NAMES = [
  "Extremely low",
  "Significantly low",
  "Somewhat low",
  "Near average",
  "Somewhat high",
  "Significantly high",
  "Extremely high",
];

/** Percentile boundaries between the seven bands. */
export const TRUE_BOUNDS = [0, 2.5, 16, 45, 55, 84, 97.5, 100];

/** Where those boundaries sit on the Volya scale, which gives every band similar height. */
export const VOLYA_DISPLAY_BOUNDS = [0, 15, 30, 45, 55, 70, 85, 100];

export const DEFAULT_CHART_SETTINGS: ChartSettings = {
  lineWidth: 2.5,
  labelSize: 13,
  gridOpacity: 0.3,
  showDots: false,
  showZoomSlider: false,
  bandOpacity: 0.24,
  bandColors: [...BAND_COLORS],
};

export interface LineStyleDef {
  key: LineStyleKey;
  label: string;
  /** SVG stroke-dasharray for swatches. */
  dash: string;
  /** ECharts lineStyle.type. */
  chart: "solid" | "dashed" | "dotted" | number[];
}

export const LINE_STYLES: LineStyleDef[] = [
  { key: "solid", label: "Solid", dash: "none", chart: "solid" },
  { key: "dashed", label: "Dashed", dash: "5 3", chart: "dashed" },
  { key: "dotted", label: "Dotted", dash: "0.5 3.5", chart: "dotted" },
  { key: "dashdot", label: "Dash-dot", dash: "7 3 0.5 3", chart: [8, 4, 2, 4] },
  { key: "long", label: "Long dash", dash: "11 4", chart: [14, 6] },
];

/** Auto line styles are handed out in this order by a series' position in its trend. */
export const AUTO_LINE_ORDER: LineStyleKey[] = LINE_STYLES.map((s) => s.key);

export interface TrendVariantDef {
  value: TrendVariant;
  label: string;
  short: string;
  tag: string;
}

export const TREND_VARIANTS: TrendVariantDef[] = [
  { value: "rawlevel", label: "Raw level", short: "Level", tag: "L" },
  { value: "annual_pct_change", label: "Annual % change", short: "Annual %", tag: "%Y" },
  { value: "quarterly_pct_change", label: "3-month % change", short: "3-month %", tag: "%3M" },
  { value: "monthly_pct_change", label: "Monthly % change", short: "Monthly %", tag: "%M" },
];

export const TREND_SCALES: { value: TrendScale; label: string }[] = [
  { value: "values", label: "Values" },
  { value: "raw_percentile", label: "Raw percentile" },
  { value: "volya_scale", label: "Volya scale" },
];

export const TREND_RANGES: { value: TrendRange; label: string }[] = [
  { value: "1", label: "1Y" },
  { value: "3", label: "3Y" },
  { value: "5", label: "5Y" },
  { value: "max", label: "Max" },
];

export const FREQUENCY_LABELS: Record<string, string> = {
  daily: "Daily",
  weekly: "Weekly",
  monthly: "Monthly",
  quarterly: "Quarterly",
  annual: "Annual",
};

export function variantDef(variant: TrendVariant): TrendVariantDef {
  return TREND_VARIANTS.find((v) => v.value === variant) ?? TREND_VARIANTS[0];
}

export function scaleLabel(scale: TrendScale): string {
  return TREND_SCALES.find((s) => s.value === scale)?.label ?? scale;
}

export function lineStyleDef(key: LineStyleKey): LineStyleDef {
  return LINE_STYLES.find((s) => s.key === key) ?? LINE_STYLES[0];
}
